/**
 * FieldModeRequestCard - Saha modu geçiş talebini kart formatında gösterir
 * (OvertimeCard.tsx ile aynı desende, sadeleştirilmiş)
 */

import React from 'react';
import { Avatar, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { FieldActivityType, FieldModeRequestDto } from '../../../../types/fieldModeRequest.types';
import { getFileUrl } from '../../../../utils/file.utils';
import ApprovalFlowTags from '../../../shared/approval/ApprovalFlowTags';

const ACTIVITY_TYPE_LABELS: Record<FieldActivityType, string> = {
  VISIT: 'Ziyaret',
  DELIVERY: 'Teslimat',
  MAINTENANCE: 'Bakım',
  OTHER: 'Diğer',
  OFFICE: 'Ofis',
};

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).locale('tr').format('DD.MM.YYYY HH:mm') : '-';

interface FieldModeRequestCardProps {
  request: FieldModeRequestDto;
  onApprove: (request: FieldModeRequestDto) => void;
  onReject: (request: FieldModeRequestDto) => void;
}

const FieldModeRequestCard: React.FC<FieldModeRequestCardProps> = ({ request, onApprove, onReject }) => {
  const getProfilePicture = () => {
    if (request.user.profilePicture && request.user.profilePicture.trim() !== '') {
      return getFileUrl(request.user.profilePicture);
    }
    if (request.user.gender === 'male') return '/assets/img/man.png';
    if (request.user.gender === 'female') return '/assets/img/woman.png';
    return undefined;
  };

  const modeLabel = request.requestedMode === 'field' ? 'Sahaya Çıkış' : 'Ofise Dönüş';
  const modeIcon = request.requestedMode === 'field' ? 'ti-car' : 'ti-building';
  const activity = request.fieldActivity;

  return (
    <div className="card border rounded-lg mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center gap-3">
            <Avatar
              size={48}
              src={getProfilePicture()}
              icon={!getProfilePicture() ? <UserOutlined /> : undefined}
              style={{ backgroundColor: !getProfilePicture() ? '#1890ff' : undefined, fontSize: '20px', flexShrink: 0 }}
            >
              {!getProfilePicture() && `${request.user.firstName[0]}${request.user.lastName[0]}`}
            </Avatar>
            <div>
              <h5 className="mb-1 fw-semibold">
                {request.user.firstName} {request.user.lastName}
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                {request.user.primaryDepartment?.name || 'Departman Yok'}
              </p>
            </div>
          </div>
          <span className="badge bg-warning-transparent text-warning">Onay Bekliyor</span>
        </div>

        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className={`ti ${modeIcon} text-primary`} style={{ fontSize: '1.25rem' }} />
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>Talep:</span>
            <span className="fw-medium">{modeLabel}</span>
          </div>

          <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '0.875rem' }}>
            <i className="ti ti-clock-hour-4" style={{ fontSize: '1rem' }} />
            <span>Talep Zamanı:</span>
            <span>{dayjs(request.createdAt).locale('tr').format('DD.MM.YYYY HH:mm')}</span>
          </div>

          {activity && (
            <div className="mt-3 p-3 rounded bg-light" style={{ fontSize: '0.875rem' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="ti ti-map-pin text-primary" style={{ fontSize: '1rem' }} />
                <span className="fw-semibold">Saha Aktivitesi</span>
                {activity.type && ACTIVITY_TYPE_LABELS[activity.type] && (
                  <Tag color="blue" className="ms-1">{ACTIVITY_TYPE_LABELS[activity.type]}</Tag>
                )}
              </div>

              <div className="mb-2">
                <span className="text-muted d-block">Ne için:</span>
                <span className="fw-medium">{activity.title}</span>
              </div>

              {activity.description && (
                <div className="mb-2">
                  <span className="text-muted d-block">Nereye / Açıklama:</span>
                  <span style={{ whiteSpace: 'pre-line' }}>{activity.description}</span>
                </div>
              )}

              <div className="mb-2">
                <span className="text-muted d-block">Planlanan Zaman:</span>
                <span>
                  {formatDateTime(activity.startDate)} - {formatDateTime(activity.endDate)}
                </span>
              </div>

              <div className={request.requestedMode === 'office' ? 'mb-2' : undefined}>
                <span className="text-muted d-block">Araç:</span>
                {activity.vehicle ? (
                  <span className="d-inline-flex align-items-center gap-2">
                    <i className="ti ti-car" />
                    <span>
                      {activity.vehicle.name} ({activity.vehicle.plateNumber})
                    </span>
                    {activity.vehicle.assignmentType === 'pool' && <Tag color="purple">Havuz</Tag>}
                  </span>
                ) : (
                  <span className="text-muted">Araç kullanılmayacak</span>
                )}
              </div>

              {request.requestedMode === 'office' && (
                <div>
                  <span className="text-muted d-block">Sonuç açıklaması:</span>
                  <span style={{ whiteSpace: 'pre-line' }}>{activity.completionNotes || '-'}</span>
                </div>
              )}
            </div>
          )}

          {request.approvalFlow && request.approvalFlow.length > 0 && (
            <div className="mt-2">
              <span className="text-muted d-block mb-1" style={{ fontSize: '0.875rem' }}>Onay Akışı:</span>
              <ApprovalFlowTags steps={request.approvalFlow} />
            </div>
          )}
        </div>

        <div className="d-flex gap-2">
          <button
            onClick={() => onApprove(request)}
            className="btn btn-success flex-fill d-flex align-items-center justify-content-center gap-2"
          >
            <i className="ti ti-check" />
            Onayla
          </button>
          <button
            onClick={() => onReject(request)}
            className="btn btn-danger flex-fill d-flex align-items-center justify-content-center gap-2"
          >
            <i className="ti ti-x" />
            Reddet
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldModeRequestCard;
